<template>
  <!-- 销售漏斗学员详情独立路由页，沿用宿主公用表格与学员详情弹层。 -->
  <div class="sales-funnel-students">
    <div class="page-heading">
      <strong>销售漏斗列表学员详情 · {{ fieldLabel }}</strong>
      <span>{{ context.beginTime }} 至 {{ context.endTime }}</span>
    </div>
    <!-- 公共表格提供刷新和列设置；仅在销售漏斗菜单上下文有效时显示当前页导出。 -->
    <StudentsTableHeader id="SalesFunnelStudentList" componentTableId="SalesFunnelStudentList"
      excel-name="销售漏斗学员详情"
      :table-height="tableHeight" :header-data="headerData" :table-data="rows"
      :table-loading="loading" :is-show-all-btn="true" :is-estimate-score="!hasValidExportMenu" :checkbox-all="false"
      :pagination="pagination" @search-data="searchFromHeader" @search-remote="searchTeacher"
      @update-list="loadPage"
      empty-text="暂无数据" element-loading-text="加载中...">
      <template v-slot:studentNamecustom="scope">
        <button v-if="!isMissingValue(scope.scope.studentName)" class="student-link" type="button"
          @click="openStudent(scope.scope)">
          {{ scope.scope.studentName }}
        </button>
        <span v-else class="missing-value">未设置</span>
      </template>
      <template v-slot:payMoneycustom="scope">
        <span v-if="isMissingValue(scope.scope.payMoney)" class="missing-value">未设置</span>
        <span v-else>{{ scope.scope.payMoney }}元</span>
      </template>
      <template v-slot:payCoursescustom="scope">
        <span v-if="isMissingValue(scope.scope.payCourses)" class="missing-value">未设置</span>
        <span v-else>{{ formatCourseClasses(scope.scope.payCourses) }}</span>
      </template>
      <template v-slot:trackCountcustom="scope">
        <span v-if="isMissingValue(scope.scope.trackCount)" class="missing-value">未设置</span>
        <span v-else>{{ scope.scope.trackCount }}</span>
      </template>
    </StudentsTableHeader>
    <PaginationTool :pagination="pagination" :is-loading="loading" :is-dialog="false"
      :page-sizes="[20, 50, 100]" @change-pagination="changePagination" />

    <!-- 点击姓名后继续使用宿主已有的学员详情弹层。 -->
    <StudentDialog v-if="isStudentDialogVisible" title="学生详情" :show.sync="isStudentDialogVisible"
      :isFooter="false" @isfullscreen="isFullscreen = $event">
      <div slot="body">
        <StudentDetailNew :isService="true" :studentId="selectedStudentId"
          :isFullscreen="isFullscreen" />
      </div>
    </StudentDialog>
  </div>
</template>

<script>
import StudentsTableHeader from 'components/StudentList';
import PaginationTool from 'components/PaginationTool';
import StudentDialog from 'components/StudentDialog';
import StudentDetailNew from '../studentsV2/studentDetailNew.vue';
import { getSalesFunnelTeacherStudentList, GetBusinessStudentsSelectListData } from '@/network/statistics';
import { GetCatetListByType } from '@/network/studentSortModule';
import { getAdminList } from '@/network/administrator';

/** 报表人数列的服务端枚举值与中文名称。 */
const fieldNames = { 1: '总登记数', 2: '掉公海数', 3: '加微数', 4: '潜在用户', 5: '意向用户', 6: '已报学员' };

/** @param {string} value 路由中的日期时间。@returns {boolean} 格式及实际日历时间均有效。 */
function isValidDateTime(value) {
  if (typeof value !== 'string') return false;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return false;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month
    && date.getUTCDate() === day && date.getUTCHours() === hour
    && date.getUTCMinutes() === minute && date.getUTCSeconds() === second;
}

/** @param {Object} item 来源分类节点。@returns {Object} 公共级联筛选可用的层级选项。 */
function mapSourceOption(item) {
  const option = { value: item.id, label: item.name };
  if (Array.isArray(item.childModel) && item.childModel.length) {
    option.children = item.childModel.map(mapSourceOption);
  }
  return option;
}

/** @param {number|string|null} value 下拉框选中的 ID。@returns {number|undefined} 可选整数，空值不传。 */
function optionalFilterId(value) {
  if (value === null || value === undefined || value === '') return undefined;
  const normalized = typeof value === 'string' ? value.trim() : value;
  if (normalized === '') return undefined;
  const id = Number(normalized);
  return Number.isInteger(id) ? id : undefined;
}

/** @param {Object} filters 当前表头条件。@returns {string} 第一个无效整数筛选项的中文名称。 */
function invalidFilterLabel(filters) {
  const labels = {
    gradeStr: '所在年级', handoverName: '教务交接老师', marketingName: '客服老师',
    operationName: '运营老师', sourceStr: '来源渠道', firstBusinessName: '首次分配老师',
    stateStr: '学员状态', studentIntentStr: '报名意向',
  };
  for (const [field, label] of Object.entries(labels)) {
    const selected = filters[field];
    const value = field === 'sourceStr' && Array.isArray(selected) ? selected[selected.length - 1] : selected;
    if (value !== null && value !== undefined && value !== '' && optionalFilterId(value) === undefined) return label;
  }
  return '';
}

/** @param {Object} row 服务端学员行。@param {Array} columns 当前列定义。@returns {Object} 仅将空白文本归一为表格可识别的空值。 */
function normalizeDisplayRow(row, columns) {
  const displayRow = { ...row };
  columns.forEach(({ prop }) => {
    if (typeof displayRow[prop] === 'string' && displayRow[prop].trim() === '') displayRow[prop] = '';
  });
  return displayRow;
}

/** 学员列表列定义；顺序对齐分配接入详情，额外字段插在相邻业务列旁。 */
const headerData = [
  { label: '学员名称', prop: 'studentName', width: '150', fixed: 'left', custom: true, inputSearch: true, studentName: '' },
  { label: '手机号', prop: 'telNumber', width: '150', inputSearch: true, telNumber: '' },
  { label: '微信 ID', prop: 'wechatId', width: '150', inputSearch: true, wechatId: '' },
  { label: '微信号', prop: 'weiXinNumber', width: '150' },
  { label: 'QQ 号', prop: 'qqNumber', width: '140', inputSearch: true, qqNumber: '' },
  { label: '缴费总金额', prop: 'payMoney', width: '140', custom: true, orZero: true },
  { label: '登记时间', prop: 'createTime', width: '180' },
  { label: '报名课程及班级', prop: 'payCourses', width: '260', custom: true },
  { label: '学员状态', prop: 'stateStr', width: '130', selectSearch: true, selectOptions: [], clearable: true },
  { label: '报名意向', prop: 'studentIntentStr', width: '130', selectSearch: true, selectOptions: [], clearable: true },
  { label: '首次分配老师', prop: 'firstBusinessName', width: '150', selectSearch: true, selectOptions: [], clearable: true, remoteMethod: true },
  { label: '所在年级', prop: 'gradeStr', width: '130', selectSearch: true, selectOptions: [], clearable: true },
  { label: '所属招生', prop: 'businessName', width: '130', selectSearch: true, selectOptions: [], clearable: true },
  { label: '教务交接老师', prop: 'handoverName', width: '150', selectSearch: true, selectOptions: [], clearable: true, remoteMethod: true },
  { label: '客服老师', prop: 'marketingName', width: '130', selectSearch: true, selectOptions: [], clearable: true, remoteMethod: true },
  { label: '运营老师', prop: 'operationName', width: '130', selectSearch: true, selectOptions: [], clearable: true, remoteMethod: true },
  { label: '来源渠道', prop: 'sourceStr', width: '150', cascader: true, selectOptions: [] },
  { label: '专科院校', prop: 'graduatedSchool', width: '170' },
  { label: '专科专业', prop: 'graduatedMajor', width: '170' },
  { label: '英语基础', prop: 'basicEnglishStr', width: '130' },
  { label: '报名时间', prop: 'signUpTime', width: '180' },
  { label: '报考院校', prop: 'applySchool', width: '170' },
  { label: '报考专业', prop: 'applyMajor', width: '170' },
  { label: '报考院校备选', prop: 'applySchoolTo', width: '180' },
  { label: '报考专业备选', prop: 'applyMajorTo', width: '180' },
  { label: '录取状态', prop: 'admissionStr', width: '130' },
  { label: '录取院校', prop: 'admissionSchool', width: '170' },
  { label: '录取专业', prop: 'admissionMajor', width: '170' },
  { label: '跟踪记录数', prop: 'trackCount', width: '130', custom: true, orZero: true },
];

export default {
  name: 'SalesFunnelStudentList',
  components: { StudentsTableHeader, PaginationTool, StudentDialog, StudentDetailNew },
  data() {
    return {
      context: { field: 0, businessId: 0, beginTime: '', endTime: '' },
      // 筛选值与表格行字段分开保存，所有条件均交由服务端分页查询。
      filters: {
        studentName: '', telNumber: '', qqNumber: '', wechatId: '',
        stateStr: null, studentIntentStr: null, gradeStr: null, sourceStr: [], businessName: null,
        firstBusinessName: null, marketingName: null, operationName: null, handoverName: null,
      },
      pagination: { currentPage: 1, pageSize: 20, total: 0 },
      rows: [],
      loading: false,
      hasPendingReload: false,
      // 公共表格的空值样式覆盖普通列；金额和次数通过自定义插槽保留有效的零值。
      headerData: headerData.map(item => ({
        ...item, noSetText: '未设置', noTooltip: true,
        selectOptions: item.selectOptions ? [] : undefined,
      })),
      isStudentDialogVisible: false,
      isFullscreen: false,
      selectedStudentId: 0,
    };
  },
  computed: {
    /** 当前统计字段的中文名称。 */
    fieldLabel() { return fieldNames[this.context.field] || ''; },
    /** 独立路由页根据窗口高度确定表格滚动区域。 */
    tableHeight() { return Math.max(300, window.innerHeight - 110); },
    /** @returns {boolean} 仅允许用销售漏斗菜单 ID 和名称记录学员当前页导出。 */
    hasValidExportMenu() {
      try {
        const menu = JSON.parse(sessionStorage.getItem('tabObj') || '{}');
        return menu.component === 'SalesFunnelList' && !!menu.name && !!menu.title;
      } catch (error) {
        return false;
      }
    },
  },
  created() {
    if (this.consumeNewWindowToken() && this.readRouteContext()) {
      this.loadPage();
      this.loadFilterOptions();
      this.loadBusinessOption();
    }
  },
  methods: {
    /** @returns {boolean} 将新窗口票据换得的令牌交给宿主统一刷新流程，并清除临时副本。 */
    consumeNewWindowToken() {
      const rawToken = sessionStorage.getItem('AdminToken');
      if (rawToken) {
        try {
          const adminToken = JSON.parse(rawToken);
          const expiresIn = Number(adminToken.expires_in);
          if (!adminToken.access_token || !adminToken.refresh_token || !Number.isFinite(expiresIn) || expiresIn <= 0) {
            this.$alert('新窗口登录状态无效，请返回报表重新打开');
            return false;
          }
          this.$store.commit('updateToken', {
            AccessToken: adminToken.access_token,
            AccessExpiresIn: expiresIn + Date.now() / 1000,
            RefreshToken: adminToken.refresh_token,
          });
        } catch (error) {
          this.$alert('新窗口登录状态无效，请返回报表重新打开');
          return false;
        } finally {
          sessionStorage.removeItem('AdminToken');
        }
      }
      if (!sessionStorage.getItem('token')) {
        this.$alert('登录状态无效，请重新登录');
        return false;
      }
      return true;
    },
    /** @returns {boolean} 仅接受六个枚举值、有效老师 ID 和真实日历时间。 */
    readRouteContext() {
      const query = this.$route.query;
      const field = Number(query.Field);
      const businessId = Number(query.BusinessId);
      const beginTime = query.BeginTime;
      const endTime = query.EndTime;
      if (!fieldNames[field] || !Number.isInteger(businessId) || businessId <= 0
        || !isValidDateTime(beginTime) || !isValidDateTime(endTime) || beginTime > endTime) {
        this.$alert('销售漏斗详情查询字段、所属招生老师或时间范围无效');
        return false;
      }
      this.context = { field, businessId, beginTime, endTime };
      return true;
    },
    /** 表头筛选只接受本页配置的字段，清空筛选时也回到第一页。@param {Object} option 公共表格的筛选事件。 */
    searchFromHeader(option) {
      if (!option || !Object.prototype.hasOwnProperty.call(this.filters, option.prop)) return;
      this.filters[option.prop] = typeof option.value === 'string' ? option.value.trim() : option.value;
      this.pagination.currentPage = 1;
      if (this.loading) {
        this.hasPendingReload = true;
        return;
      }
      this.loadPage();
    },
    /** 加载报名意向、年级、来源与状态的授权选项；失败时保留已成功加载的列表。 */
    async loadFilterOptions() {
      const results = await Promise.allSettled([
        GetCatetListByType(3), GetCatetListByType(4),
        GetCatetListByType(2), GetBusinessStudentsSelectListData(),
      ]);
      const fields = ['studentIntentStr', 'gradeStr', 'sourceStr', 'stateStr'];
      results.forEach((result, index) => {
        // 状态接口直接返回 studentStateList，其余三个分类接口使用 code/result 包装。
        const response = result.status === 'fulfilled' ? result.value : null;
        const isValidResponse = response && (index === 3
          ? (response.code == null || response.code === 1)
            && Array.isArray((response.result || response).studentStateList)
          : response.code === 1);
        if (!isValidResponse) {
          this.$alert(`${this.headerData.find(item => item.prop === fields[index]).label}筛选项加载失败`);
          return;
        }
        const data = response.result || response;
        const options = index === 3 ? data.studentStateList : data;
        if (!Array.isArray(options)) return;
        const header = this.headerData.find(item => item.prop === fields[index]);
        header.selectOptions = index === 2 ? options.map(mapSourceOption)
          : options.map(item => ({
            key: item.key ?? item.id, id: item.key ?? item.id, value: item.value || item.name,
          }));
      });
    },
    /** 从授权管理员接口按详情老师 ID 取得名称，空学员页也能显示所属招生筛选项。 */
    async loadBusinessOption() {
      try {
        const response = await getAdminList({
          ids: [this.context.businessId], keyWord: '', pageSize: 50, adminTypeIds: [],
        });
        if (!response || response.code !== 1 || !Array.isArray(response.result)) {
          this.$alert((response && response.message) || '所属招生筛选项加载失败');
          return;
        }
        const teacher = response.result.find(item => Number(item.id) === this.context.businessId);
        if (!teacher) return;
        const header = this.headerData.find(item => item.prop === 'businessName');
        header.selectOptions = [{ key: teacher.id, value: teacher.name }];
      } catch (error) {
        this.$alert('所属招生筛选项加载失败');
      }
    },
    /** @param {Object} header 表头老师筛选配置；根据已授权角色远程查找老师。 */
    async searchTeacher(header) {
      const roleKeys = {
        firstBusinessName: 'businessRoleId', marketingName: 'marketingRoleId',
        operationName: 'operationRoleId', handoverName: 'educationalRoleId',
      };
      const roleId = this.$store.state.allConfig && this.$store.state.allConfig[roleKeys[header.prop]];
      if (!roleId) return;
      const keyword = header.value || '';
      try {
        const response = await getAdminList({
          ids: [], keyWord: keyword, pageSize: 50, adminTypeIds: [roleId],
        });
        if (!response || response.code !== 1) {
          this.$alert((response && response.message) || `${header.label}筛选项加载失败`);
          return;
        }
        // 远程输入连续变化时，只接受与当前关键字一致的选项响应。
        if (header.value !== keyword) return;
        header.selectOptions = Array.isArray(response.result) ? response.result : [];
      } catch (error) {
        this.$alert(`${header.label}筛选项加载失败`);
      }
    },
    /** 公共分页组件修改页码或每页条数后，按当前服务端筛选条件重新加载。 */
    changePagination() {
      if (this.loading) {
        this.hasPendingReload = true;
        return;
      }
      this.loadPage();
    },
    /** @returns {Promise<void>} 使用统一请求层查询授权学员的当前页。 */
    async loadPage() {
      if (this.loading) return;
      const invalidLabel = invalidFilterLabel(this.filters);
      if (invalidLabel) {
        this.rows = [];
        this.pagination.total = 0;
        this.$alert(`${invalidLabel}筛选值无效，请重新选择`);
        return;
      }
      this.loading = true;
      try {
        const response = await getSalesFunnelTeacherStudentList({
          Type: this.context.field,
          BeginTime: this.context.beginTime,
          EndTime: this.context.endTime,
          BusinessId: this.context.businessId,
          // 筛选键对应招生老师漏斗学员明细契约，表头字段仅用于页面展示。
          Name: this.filters.studentName || undefined,
          TelNumber: this.filters.telNumber || undefined,
          QqNumber: this.filters.qqNumber || undefined,
          WechatId: this.filters.wechatId || undefined,
          Grade: optionalFilterId(this.filters.gradeStr),
          HandoverId: optionalFilterId(this.filters.handoverName),
          MarketingId: optionalFilterId(this.filters.marketingName),
          OperationId: optionalFilterId(this.filters.operationName),
          Source: (Array.isArray(this.filters.sourceStr) ? this.filters.sourceStr : [this.filters.sourceStr])
            .map(optionalFilterId).filter(id => id !== undefined),
          FirstBusinessId: optionalFilterId(this.filters.firstBusinessName),
          State: optionalFilterId(this.filters.stateStr),
          StudentIntent: optionalFilterId(this.filters.studentIntentStr),
          PageIndex: this.pagination.currentPage,
          PageSize: this.pagination.pageSize,
        });
        // 请求期间用户可继续调整表头或分页；旧响应不能覆盖新条件的结果。
        if (this.hasPendingReload) return;
        if (!response || response.code !== 1) {
          this.rows = [];
          this.pagination.total = 0;
          this.$alert((response && response.message) || '查询销售漏斗学员失败');
          return;
        }
        const result = response.result || {};
        this.rows = Array.isArray(result.data)
          ? result.data.map(row => normalizeDisplayRow(row, this.headerData)) : [];
        this.pagination.total = result.totalRecords || 0;
        this.syncBusinessOptionFromRows();
      } catch (error) {
        if (!this.hasPendingReload) {
          this.rows = [];
          this.pagination.total = 0;
          this.$alert('查询销售漏斗学员失败，请稍后重试');
        }
      } finally {
        this.loading = false;
        if (this.hasPendingReload) {
          this.hasPendingReload = false;
          this.loadPage();
        }
      }
    },
    /** 当前页有老师名称时补足所属招生候选；始终固定为详情路由的老师 ID。 */
    syncBusinessOptionFromRows() {
      const businessRow = this.rows.find(item => item.businessName);
      if (!businessRow) return;
      const businessHeader = this.headerData.find(item => item.prop === 'businessName');
      businessHeader.selectOptions = [{ key: this.context.businessId, value: businessRow.businessName }];
    },
    /** @param {Object} row 授权学员行；复用宿主已有的学员详情弹层。 */
    openStudent(row) {
      if (!Number.isInteger(row.id) || row.id <= 0) return;
      this.selectedStudentId = row.id;
      this.isStudentDialogVisible = true;
    },
    /** @param {Array} payCourses 已报名课程。@returns {string} 编码渲染的课程及班级文本。 */
    formatCourseClasses(payCourses) {
      if (!Array.isArray(payCourses)) return '';
      return payCourses.map(item => `${item.title || '未命名课程'} / ${item.gradeName || '未分班'}`).join('；');
    },
    /** @param {*} value 单元格原值。@returns {boolean} 空文本、空列表或缺失值。 */
    isMissingValue(value) {
      return value === null || value === undefined
        || (typeof value === 'string' && value.trim() === '')
        || (Array.isArray(value) && value.length === 0);
    },
  },
};
</script>

<style scoped>
/* 独立详情页标题展示统计字段与查询范围，筛选输入位于公共表格的列头。 */
.sales-funnel-students { padding: 16px; }
.page-heading { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; margin-bottom: 16px; }
.page-heading span { color: #606266; }
/* 姓名作为学员详情入口，使用按钮以便键盘操作，接口文本不按 HTML 解析。 */
.student-link { padding: 0; border: 0; background: none; color: #409eff; cursor: pointer; font: inherit; }
.student-link:hover { text-decoration: underline; }
/* 自定义单元格的空值与公共表格灰色占位保持一致。 */
.missing-value { color: #909399; }
</style>
